/**
 * Flow detail rank lookups — mirrors PreviewCompactLayersPanel grouping.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, FlowOptimizerItemPreview } from '@trafficpulse/shared';

import { dedupePreviewItems, previewItemKey, rankPreviewItems } from './preview-sort';

export type FlowPreviewRankLookups = {
  get: (layer: FlowLayer, itemId: string, pathId?: string) => number | undefined;
};

function groupByPathId(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): Map<string, FlowOptimizerItemPreview[]> {
  const map = new Map<string, FlowOptimizerItemPreview[]>();
  for (const item of items) {
    const pathId = item.pathId ?? item.parentId ?? '';
    if (!pathId) continue;
    const list = map.get(pathId) ?? [];
    list.push(item);
    map.set(pathId, list);
  }
  return map;
}

/** Group paths by their sibling pool (parentId); items without one share ''. */
function groupPathsByParent(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): Map<string, FlowOptimizerItemPreview[]> {
  const map = new Map<string, FlowOptimizerItemPreview[]>();
  for (const item of items) {
    const key = item.parentId ?? '';
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }
  return map;
}

function rankByItemId(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): Map<string, number> {
  const ranks = rankPreviewItems(items);
  const byId = new Map<string, number>();
  for (const item of items) {
    const rank = ranks.get(previewItemKey(item));
    if (rank != null) byId.set(item.itemId, rank);
  }
  return byId;
}

export function buildFlowPreviewRankLookups(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): FlowPreviewRankLookups {
  // Paths rank within their group (parentId = "flow:default" / "flow:group:<id>"),
  // not flow-wide — so each group's #N badges run 1..N with no cross-group gaps.
  const pathRanks = new Map<string, number>();
  for (const group of groupPathsByParent(items.filter((i) => i.layer === 'paths')).values()) {
    for (const [id, rank] of rankByItemId(group)) pathRanks.set(id, rank);
  }

  const landerRanksByPath = new Map<string, Map<string, number>>();
  for (const [pathId, group] of groupByPathId(items.filter((i) => i.layer === 'landers'))) {
    landerRanksByPath.set(pathId, rankByItemId(dedupePreviewItems(group)));
  }

  const offerRanksByPath = new Map<string, Map<string, number>>();
  for (const [pathId, group] of groupByPathId(items.filter((i) => i.layer === 'offers'))) {
    offerRanksByPath.set(pathId, rankByItemId(dedupePreviewItems(group)));
  }

  return {
    get(layer, itemId, pathId) {
      if (layer === 'paths') return pathRanks.get(itemId);
      if (!pathId) return undefined;
      if (layer === 'landers') return landerRanksByPath.get(pathId)?.get(itemId);
      if (layer === 'offers') return offerRanksByPath.get(pathId)?.get(itemId);
      return undefined;
    },
  };
}
